import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
// eslint-disable-next-line max-len
import { type EventApplicationModuleEmailContentSpecsFragment } from '../../../../api/fragments/EventApplicationModuleEmailContentSpecs';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    DeleteEventApplicationModuleAssetDocument,
    type DeleteEventApplicationModuleAssetMutation,
    type DeleteEventApplicationModuleAssetMutationVariables,
} from '../../../../api/mutations/deleteEventApplicationModuleAsset';
import {
    UploadEventApplicationModuleAssetDocument,
    type UploadEventApplicationModuleAssetMutation,
    type UploadEventApplicationModuleAssetMutationVariables,
} from '../../../../api/mutations/uploadEventApplicationModuleAsset';
import { type EmailContentUpdateType, EventModuleAsset } from '../../../../api/types';
import { type EventApplicationModuleEmailFormValues } from './type';

type UploadParams = {
    moduleId: string;
    upload: File | UploadFileWithPreviewFormDataFragment;
    type: EventModuleAsset;
    emailContentUpdateType: EmailContentUpdateType;
    dealerId?: string;
};

const useHandleEventApplicationModuleAssets = () => {
    const apolloClient = useApolloClient();

    const updateOrDelete = useCallback(
        (
            { type, moduleId, upload, emailContentUpdateType, dealerId }: UploadParams,
            initialValue: UploadFileWithPreviewFormDataFragment
        ) => {
            const uploads = [];

            if (upload instanceof File) {
                // upload a new file
                uploads.push(
                    apolloClient.mutate<
                        UploadEventApplicationModuleAssetMutation,
                        UploadEventApplicationModuleAssetMutationVariables
                    >({
                        mutation: UploadEventApplicationModuleAssetDocument,
                        variables: {
                            id: moduleId,
                            type,
                            upload,
                            emailContentUpdateType,
                            dealerId,
                        },
                    })
                );
            } else if (upload === null && isObject(initialValue)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<
                        DeleteEventApplicationModuleAssetMutation,
                        DeleteEventApplicationModuleAssetMutationVariables
                    >({
                        mutation: DeleteEventApplicationModuleAssetDocument,
                        variables: { id: moduleId, type, emailContentUpdateType, dealerId },
                    })
                );
            }

            return uploads;
        },
        [apolloClient]
    );

    return useCallback(
        (
            moduleId: string,
            values: EventApplicationModuleEmailFormValues,
            emailContentUpdateType: EmailContentUpdateType,
            initialValues?: EventApplicationModuleEmailContentSpecsFragment,
            dealerId?: string
        ) => {
            const { submitOrder } = values;

            const uploads = [
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: submitOrder.introImage,
                        type: EventModuleAsset.SubmitOrderIntro,
                        emailContentUpdateType,
                        dealerId,
                    },
                    initialValues?.submitOrder.introImage
                ),
            ];

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [updateOrDelete]
    );
};

export default useHandleEventApplicationModuleAssets;
